/* Chroma theme — global.js (no dependencies) */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const routes = window.routes || { root: '/', cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change' };
  const strings = window.themeStrings || {};
  const settings = window.themeSettings || {};

  /* ---------- Money ---------- */
  function formatMoney(cents, format = settings.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, dec = 2, thou = ',', d = '.') => {
      if (isNaN(n) || n == null) return '0';
      const parts = (n / 100).toFixed(dec).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thou) + (parts[1] ? d + parts[1] : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------- Focus trap (drawers / dialogs) ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container) {
    releaseFocus();
    trapFocus.handler = (e) => {
      if (e.key !== 'Tab') return;
      const els = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!els.length) return;
      const first = els[0]; const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapFocus.handler);
  }
  function releaseFocus() { if (trapFocus.handler) document.removeEventListener('keydown', trapFocus.handler); trapFocus.handler = null; }

  /* ---------- Toast ---------- */
  function toast(msg) {
    let t = $('.toast');
    if (!t) { t = document.createElement('div'); t.className = 'toast'; t.setAttribute('role', 'status'); document.body.append(t); }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add('is-visible'));
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-visible'), 3200);
  }

  /* ---------- Reveal on scroll ---------- */
  let io;
  function reveal(root = document) {
    const els = $$('[data-reveal]:not(.is-visible)', root);
    if (!('IntersectionObserver' in window) || document.body.classList.contains('no-anim')) {
      els.forEach((e) => e.classList.add('is-visible'));
      return;
    }
    io = io || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    els.forEach((e) => io.observe(e));
  }

  /* ---------- Sticky header (hide on scroll down) ---------- */
  function initHeader() {
    const wrap = $('.header-wrap--sticky');
    if (!wrap) return;
    let last = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        wrap.classList.toggle('is-scrolled', y > 10);
        const menuOpen = wrap.querySelector('.header__item:hover');
        wrap.classList.toggle('is-hidden', y > last && y > 320 && !menuOpen);
        last = y;
        ticking = false;
      });
    }, { passive: true });

    const toggle = $('[data-search-toggle]', wrap);
    const panel = $('#SearchPanel');
    if (toggle && panel) {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        panel.hidden = !panel.hidden;
        toggle.setAttribute('aria-expanded', String(!panel.hidden));
        if (!panel.hidden) $('input', panel)?.focus();
      });
    }
  }

  /* ---------- Menu drawer ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.openers = $$('[data-menu-open]');
      this.openers.forEach((b) => b.addEventListener('click', () => this.open()));
      $$('[data-menu-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.removeAttribute('inert');
      document.body.classList.add('overflow-hidden');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      trapFocus(this);
      setTimeout(() => $('[data-menu-close]', this)?.focus(), 60);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      document.body.classList.remove('overflow-hidden');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      releaseFocus();
      this.openers[0]?.focus();
    }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Cart helpers ---------- */
  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
      el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump');
    });
    const tpl = Number(count) === 1 ? strings.cartOne : strings.cartOther;
    if (tpl) $$('[data-cart-link]').forEach((a) => a.setAttribute('aria-label', tpl.replace('__COUNT__', count)));
  }

  function swapInner(container, html, selector) {
    if (!container || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(selector);
    const current = container.querySelector(selector);
    if (fresh && current) current.replaceWith(fresh);
  }

  async function changeLine(line, quantity, sectionIds) {
    const res = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: sectionIds, sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
    return data;
  }

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || 'cart-drawer';
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cart-toggle]');
        if (t) { e.preventDefault(); this.open(t); }
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
        const rm = e.target.closest('[data-line-remove]');
        if (rm) { e.preventDefault(); this.change(rm.dataset.lineRemove, 0); }
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-line-qty]')) this.change(e.target.dataset.line, e.target.value);
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      if (opener) this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.removeAttribute('inert');
      document.body.classList.add('overflow-hidden');
      trapFocus(this);
      setTimeout(() => $('.drawer__close', this)?.focus(), 80);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      document.body.classList.remove('overflow-hidden');
      releaseFocus();
      this.opener?.focus();
    }
    render(html) {
      swapInner(this, html, '[data-drawer-inner]');
      if (this.classList.contains('is-open') && !this.contains(document.activeElement)) $('.drawer__close', this)?.focus();
    }
    async refresh() {
      const res = await fetch(`${routes.root.replace(/\/$/, '')}/?sections=${this.sectionId}`);
      const data = await res.json();
      this.render(data[this.sectionId]);
    }
    async change(line, qty) {
      this.classList.add('is-loading');
      try {
        const data = await changeLine(line, qty, [this.sectionId]);
        this.render(data.sections?.[this.sectionId]);
        updateCount(data.item_count);
      } catch (err) { toast(err.message); }
      finally { this.classList.remove('is-loading'); }
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------- Cart page ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-line-qty]')) this.change(e.target.dataset.line, e.target.value);
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-line-remove]');
        if (rm) { e.preventDefault(); this.change(rm.dataset.lineRemove, 0); }
      });
    }
    async change(line, qty) {
      this.classList.add('is-loading');
      this.style.opacity = '.6';
      try {
        const data = await changeLine(line, qty, [this.sectionId]);
        swapInner(this, data.sections?.[this.sectionId], '[data-cart-inner]');
        updateCount(data.item_count);
      } catch (err) { toast(err.message); }
      finally { this.classList.remove('is-loading'); this.style.opacity = ''; }
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      $$('button', this).forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const prev = this.input.value;
        if (b.name === 'plus') this.input.stepUp(); else this.input.stepDown();
        if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.btn = $('[type="submit"]', this.form);
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      const drawer = $('cart-drawer');
      if (settings.cartType !== 'drawer' || !drawer) return; // fall back to normal POST → /cart
      e.preventDefault();
      if (!this.btn || this.btn.classList.contains('is-loading')) return;
      this.btn.classList.add('is-loading');
      this.btn.setAttribute('aria-busy', 'true');
      const err = $('[data-form-error]', this);
      if (err) err.hidden = true;
      const fd = new FormData(this.form);
      fd.append('sections', drawer.sectionId);
      fd.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: fd
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message || strings.error);
        if (data.sections) drawer.render(data.sections[drawer.sectionId]); else await drawer.refresh();
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        updateCount(cart.item_count);
        this.btn.classList.add('is-added');
        setTimeout(() => this.btn.classList.remove('is-added'), 1600);
        drawer.open(this.btn);
      } catch (error) {
        if (err) { err.textContent = error.message; err.hidden = false; } else toast(error.message);
      } finally {
        this.btn.classList.remove('is-loading');
        this.btn.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      this.thumbs = $$('[data-thumb]', this);
      if (!this.track) return;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const slides = Array.from(this.track.children);
          const x = this.track.scrollLeft;
          let idx = 0; let best = Infinity;
          slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - this.track.offsetLeft - x); if (d < best) { best = d; idx = i; } });
          this.setActive(idx);
        });
      }, { passive: true });
    }
    goTo(i) {
      const s = this.track.children[i];
      if (!s) return;
      this.track.scrollTo({ left: s.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: 'smooth' });
      this.setActive(i);
    }
    goToMedia(id) {
      const slides = Array.from(this.track.children);
      const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.goTo(i);
    }
    setActive(i) {
      this.thumbs.forEach((t, n) => { t.classList.toggle('is-active', n === i); t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('[data-variants]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.section = this.closest('[data-product-section]') || document;
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selected() { return $$('fieldset', this).map((fs) => ($('input:checked', fs) || {}).value); }
    markAvailability() {
      const sel = this.selected();
      $$('fieldset', this).forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const test = sel.slice(); test[i] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, n) => n > i ? true : o === test[n]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    onChange() {
      const sel = this.selected();
      $$('fieldset', this).forEach((fs, i) => { const out = $('[data-selected-value]', fs); if (out) out.textContent = sel[i]; });
      this.markAvailability();
      const v = this.variants.find((vr) => vr.options.every((o, i) => o === sel[i]));
      const sec = this.section;
      const btns = $$('[data-add-btn]', sec);
      const texts = $$('[data-add-text]', sec);
      if (!v) {
        btns.forEach((b) => { b.disabled = true; });
        texts.forEach((t) => { t.textContent = strings.unavailable; });
        return;
      }
      $$('input[name="id"]', sec).forEach((i) => { i.value = v.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
      btns.forEach((b) => { b.disabled = !v.available; });
      texts.forEach((t) => { t.textContent = v.available ? strings.addToCart : strings.soldOut; });
      $$('[data-price]', sec).forEach((p) => {
        const onSale = v.compare_at_price && v.compare_at_price > v.price;
        p.classList.toggle('price--sale', !!onSale);
        let html = `<span class="price__current">${formatMoney(v.price)}</span>`;
        if (onSale) {
          const pct = Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
          html += `<s class="price__compare">${formatMoney(v.compare_at_price)}</s><span class="price__save">-${pct}%</span>`;
        }
        p.innerHTML = html;
      });
      if (v.featured_media) $('media-gallery', sec)?.goToMedia(v.featured_media.id);
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Sticky add-to-cart (mobile) ---------- */
  function initStickyATC() {
    const bar = $('[data-sticky-atc]');
    const main = $('[data-main-buy]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
    }).observe(main);
    $('[data-sticky-submit]', bar)?.addEventListener('click', () => {
      const form = $('form[data-main-form]');
      if (form?.requestSubmit) form.requestSubmit(); else form?.submit();
    });
  }

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = () => fetch(this.dataset.url).then((r) => r.text()).then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) { this.innerHTML = fresh.innerHTML; reveal(this); }
        else this.closest('.shopify-section')?.setAttribute('hidden', '');
      }).catch(() => {});
      if (!('IntersectionObserver' in window)) return load();
      const obs = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect(); load();
      }, { rootMargin: '0px 0px 500px 0px' });
      obs.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection filters ---------- */
  function initFilters() {
    const form = $('[data-filter-form]');
    if (!form) return;
    let timer;
    form.addEventListener('change', (e) => {
      if (e.target.type === 'number') return; // price uses Apply / blur
      submitFilters(form);
    });
    form.addEventListener('submit', (e) => { e.preventDefault(); submitFilters(form); });
    $$('input[type="number"]', form).forEach((i) => i.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => submitFilters(form), 900); }));
    // close other open filter dropdowns
    document.addEventListener('click', (e) => {
      $$('details.filter[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
  }
  function submitFilters(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
    window.location.search = params.toString();
  }

  /* ---------- Newsletter: scroll to message after post ---------- */
  function initForms() {
    const msg = $('[data-form-status]');
    if (msg) msg.scrollIntoView({ block: 'center' });
  }

  /* ---------- Pause / play animations (WCAG 2.2.2) ---------- */
  function initMotionToggle() {
    const btn = $('[data-motion-toggle]');
    if (!btn) return;
    const set = (paused) => {
      document.documentElement.classList.toggle('motion-paused', paused);
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', paused ? strings.playMotion : strings.pauseMotion);
      try { localStorage.setItem('chroma-motion-paused', paused ? '1' : '0'); } catch (e) { /* storage unavailable */ }
    };
    let saved = false;
    try { saved = localStorage.getItem('chroma-motion-paused') === '1'; } catch (e) { /* storage unavailable */ }
    if (saved) set(true);
    btn.addEventListener('click', () => set(!document.documentElement.classList.contains('motion-paused')));
  }

  /* ---------- Desktop dropdowns: close on Escape ---------- */
  function initDropdowns() {
    $$('.header__item').forEach((item) => {
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.contains(document.activeElement)) { $('.header__link', item)?.focus(); item.classList.add('is-closed'); }
      });
      item.addEventListener('focusout', () => item.classList.remove('is-closed'));
      item.addEventListener('mouseleave', () => item.classList.remove('is-closed'));
    });
  }

  /* ---------- Accessibility menu ---------- */
  function initA11y() {
    const root = $('[data-a11y]');
    if (!root) return;
    const html = document.documentElement;
    const fab = $('[data-a11y-open]', root);
    const panel = $('#A11yPanel', root);
    const sizeOut = $('[data-a11y-size-value]', root);
    const KEY = 'chroma-a11y';
    let state = {};
    try { state = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { state = {}; }
    const save = () => { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* storage unavailable */ } };
    const apply = () => {
      for (let i = 1; i <= 4; i++) html.classList.toggle(`a11y-size-${i}`, state.size === i);
      $$('[data-a11y-toggle]', root).forEach((b) => {
        const k = b.dataset.a11yToggle;
        html.classList.toggle(`a11y-${k}`, !!state[k]);
        b.setAttribute('aria-pressed', String(!!state[k]));
      });
      html.classList.toggle('motion-paused', !!state.motion);
      const s = state.size || 0;
      sizeOut.textContent = `${100 + s * 12.5}%`.replace('.5%', '%');
      $('[data-a11y-size="-1"]', root).disabled = s <= 0;
      $('[data-a11y-size="1"]', root).disabled = s >= 4;
    };
    const open = () => {
      panel.hidden = false; fab.setAttribute('aria-expanded', 'true');
      trapFocus(panel);
      setTimeout(() => $('[data-a11y-close]', panel)?.focus(), 30);
    };
    const close = (refocus = true) => {
      panel.hidden = true; fab.setAttribute('aria-expanded', 'false');
      releaseFocus();
      if (refocus) fab.focus();
    };
    fab.addEventListener('click', () => (panel.hidden ? open() : close()));
    $('[data-a11y-close]', root).addEventListener('click', () => close());
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) close(); });
    document.addEventListener('click', (e) => { if (!panel.hidden && !root.contains(e.target)) close(false); });
    $$('[data-a11y-toggle]', root).forEach((b) => b.addEventListener('click', () => {
      const k = b.dataset.a11yToggle; state[k] = !state[k]; save(); apply();
    }));
    $$('[data-a11y-size]', root).forEach((b) => b.addEventListener('click', () => {
      state.size = Math.max(0, Math.min(4, (state.size || 0) + Number(b.dataset.a11ySize))); save(); apply();
    }));
    $('[data-a11y-reset]', root).addEventListener('click', () => { state = {}; save(); apply(); });
    apply();
  }

  /* ---------- Cookie consent (Shopify Customer Privacy API) ---------- */
  function initCookies() {
    const el = $('[data-cookie-banner]');
    if (!el) return;
    const KEY = 'chroma-consent';
    const prefs = $('[data-cookie-prefs]', el);
    const manageBtn = $('[data-cookie-manage]', el);
    const saveBtn = $('[data-cookie-save]', el);
    const acceptBtn = $('[data-cookie-accept]', el);
    const stored = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; } };
    const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
    const setOffset = () => document.documentElement.style.setProperty('--cookie-h', `${el.offsetHeight + 28}px`);
    const show = (manage = false) => {
      el.hidden = false;
      togglePrefs(manage);
      const c = stored();
      if (c) $$('[data-consent]', el).forEach((i) => { i.checked = !!c[i.dataset.consent]; });
      requestAnimationFrame(() => { el.classList.add('is-visible'); document.documentElement.classList.add('has-cookie-banner'); setOffset(); });
    };
    const hide = () => {
      el.classList.remove('is-visible');
      document.documentElement.classList.remove('has-cookie-banner');
      setTimeout(() => { el.hidden = true; }, 500);
    };
    const togglePrefs = (open) => {
      prefs.hidden = !open; saveBtn.hidden = !open; acceptBtn.hidden = open;
      manageBtn.setAttribute('aria-expanded', String(open));
      if (!el.hidden) setOffset();
    };
    const decide = (c) => {
      try { localStorage.setItem(KEY, JSON.stringify(c)); } catch (e) { /* storage unavailable */ }
      const cp = privacy();
      if (cp && cp.setTrackingConsent) {
        cp.setTrackingConsent({ analytics: c.analytics, marketing: c.marketing, preferences: c.preferences, sale_of_data: c.marketing }, () => {});
      }
      document.dispatchEvent(new CustomEvent('chroma:consent', { detail: c }));
      hide();
    };
    acceptBtn.addEventListener('click', () => decide({ analytics: true, marketing: true, preferences: true }));
    $('[data-cookie-reject]', el).addEventListener('click', () => decide({ analytics: false, marketing: false, preferences: false }));
    manageBtn.addEventListener('click', () => togglePrefs(prefs.hidden));
    saveBtn.addEventListener('click', () => {
      const c = {}; $$('[data-consent]', el).forEach((i) => { c[i.dataset.consent] = i.checked; }); decide(c);
    });
    $$('[data-cookie-open]').forEach((b) => b.addEventListener('click', () => { show(true); $('[data-cookie-manage]', el).focus(); }));
    window.addEventListener('resize', () => { if (!el.hidden) setOffset(); });

    const shouldShow = () => {
      if (el.hasAttribute('data-design-mode')) return true;
      const cp = privacy();
      if (el.dataset.mode === 'shopify' && cp && cp.shouldShowBanner) return cp.shouldShowBanner();
      if (cp && cp.currentVisitorConsent) {
        const v = cp.currentVisitorConsent();
        if (v && (v.analytics === 'yes' || v.analytics === 'no')) return false;
      }
      return !stored();
    };
    const start = () => { if (shouldShow()) setTimeout(() => show(false), 600); };
    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => start());
    } else start();
  }

  /* ---------- "Do not sell or share" (CCPA / CPRA) ---------- */
  function initDoNotSell() {
    const btn = $('[data-dns]');
    if (!btn) return;
    const item = btn.closest('[data-dns-item]');
    const cp = () => window.Shopify && window.Shopify.customerPrivacy;
    const reveal = () => {
      const p = cp();
      if (btn.dataset.mode === 'regions' && p && p.saleOfDataRegion) item.hidden = !p.saleOfDataRegion();
    };
    if (window.Shopify && window.Shopify.loadFeatures) window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], reveal);
    btn.addEventListener('click', () => {
      const p = cp();
      if (p && p.setTrackingConsent) p.setTrackingConsent({ sale_of_data: false }, () => {});
      try {
        const c = JSON.parse(localStorage.getItem('chroma-consent') || '{}');
        c.marketing = false; c.sale_of_data = false;
        localStorage.setItem('chroma-consent', JSON.stringify(c));
      } catch (e) { /* storage unavailable */ }
      toast(btn.dataset.done || 'OK');
    });
  }

  /* ---------- Withdrawal function (two-step) ---------- */
  class WithdrawalForm extends HTMLElement {
    connectedCallback() {
      const stamp = $('[data-withdrawal-stamp]', this);
      if (stamp) {
        try { stamp.textContent = sessionStorage.getItem('chroma-withdrawal-stamp') || ''; } catch (e) { /* storage unavailable */ }
        return;
      }
      this.step1 = $('[data-step="1"]', this);
      this.step2 = $('[data-step="2"]', this);
      if (!this.step1) return;
      $('[data-withdrawal-next]', this).addEventListener('click', () => this.next());
      $('[data-withdrawal-back]', this).addEventListener('click', () => this.back());
      $('form', this).addEventListener('submit', () => {
        const now = new Date();
        const iso = now.toISOString();
        $('[data-withdrawal-time]', this).value = `${now.toLocaleString()} (${iso})`;
        const label = this.dataset.receivedAt || 'Submitted on';
        try { sessionStorage.setItem('chroma-withdrawal-stamp', `${label}: ${now.toLocaleString()} · ${$('#WdOrder', this).value}`); } catch (e) { /* storage unavailable */ }
      });
    }
    next() {
      const fields = $$('input, textarea', this.step1);
      for (const f of fields) { if (!f.checkValidity()) { f.reportValidity(); return; } }
      const dl = $('[data-withdrawal-summary]', this);
      dl.innerHTML = '';
      fields.forEach((f) => {
        const lab = $(`label[for="${f.id}"]`, this);
        if (!lab) return;
        const val = f.value.trim() || (f.id === 'WdItems' ? (this.dataset.wholeOrder || '—') : '');
        if (!val) return;
        const dt = document.createElement('dt'); dt.textContent = lab.textContent.replace('*', '').trim();
        const dd = document.createElement('dd'); dd.textContent = val;
        dl.append(dt, dd);
      });
      this.step1.hidden = true; this.step2.hidden = false;
      $('[data-step2-heading]', this).focus();
    }
    back() { this.step2.hidden = true; this.step1.hidden = false; $('input', this.step1).focus(); }
  }
  if (!customElements.get('withdrawal-form')) customElements.define('withdrawal-form', WithdrawalForm);

  /* ---------- Boot ---------- */
  function boot() {
    reveal();
    initHeader();
    initStickyATC();
    initFilters();
    initForms();
    initMotionToggle();
    initA11y();
    initCookies();
    initDoNotSell();
    initDropdowns();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));

  window.Chroma = { formatMoney, toast, reveal, updateCount };
})();
